/**
 * Global gross↔net display options — ONE control for the whole app, sitting in
 * the sticky top bar next to the date range (founder 2026-06-21: "all that near
 * the top… not in the sidebar"). Defaults to the TRUE / net view:
 *   • refunds  — subtracted (show what you actually kept)
 *   • sales tax — excluded (pass-through, not your money)
 *   • shipping  — included (what customers paid to ship counts as revenue)
 * Flipping a toggle re-runs every number on that basis. Mirrors the wholesale
 * toggle: persist per browser + invalidate every query. The basis rides along on
 * every API request as query params (only the NON-default ones), which the
 * backend reads with parseDisplayOpts to adjust the revenue / refund math.
 */

import { createContext, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setGlobalQuery } from "./api-client.js";
import { invalidateFilterQueries } from "./query-invalidation.js";

export interface DisplayOpts {
  refunds: "net" | "gross"; // net (default) = subtract refunds
  tax: "exclude" | "include"; // exclude (default) = revenue without sales tax
  shipping: "include" | "exclude"; // include (default) = shipping counts as revenue
}

export const DEFAULT_DISPLAY: DisplayOpts = { refunds: "net", tax: "exclude", shipping: "include" };

const LS_KEY = "dd-display-opts";

interface DisplayCtx extends DisplayOpts {
  set: (patch: Partial<DisplayOpts>) => void;
}

const Ctx = createContext<DisplayCtx | null>(null);

function load(): DisplayOpts {
  if (typeof localStorage === "undefined") return DEFAULT_DISPLAY;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_DISPLAY;
    const p = JSON.parse(raw) as Partial<DisplayOpts>;
    return {
      refunds: p.refunds === "gross" ? "gross" : "net",
      tax: p.tax === "include" ? "include" : "exclude",
      shipping: p.shipping === "exclude" ? "exclude" : "include",
    };
  } catch {
    return DEFAULT_DISPLAY;
  }
}

/** Only the params that DIFFER from default — keeps URLs/caches clean at the
 *  default "net" view (no params), and the backend treats absent = default. */
function toQuery(o: DisplayOpts): Record<string, string | undefined> {
  return {
    refunds: o.refunds === "gross" ? "gross" : undefined,
    tax: o.tax === "include" ? "include" : undefined,
    shipping: o.shipping === "exclude" ? "exclude" : undefined,
  };
}

// Seed the api client at module load so the very FIRST requests already carry a
// saved non-default basis (before the provider's effect runs).
setGlobalQuery(toQuery(load()));

export function DisplayOptsProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const qc = useQueryClient();
  const [opts, setOpts] = useState<DisplayOpts>(load);

  useEffect(() => {
    setGlobalQuery(toQuery(opts));
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(opts));
    } catch {
      /* storage unavailable — basis stays in-memory */
    }
  }, [opts]);

  const set = (patch: Partial<DisplayOpts>): void => {
    setOpts((cur) => {
      const next = { ...cur, ...patch };
      setGlobalQuery(toQuery(next)); // params updated BEFORE the refetch below
      void invalidateFilterQueries(qc); // re-run every DATA number on the new basis (not the shell/gate — that blanks the page)
      return next;
    });
  };

  return <Ctx.Provider value={{ ...opts, set }}>{children}</Ctx.Provider>;
}

/** Safe outside a provider (returns net defaults) so cards never crash. */
export function useDisplayOpts(): DisplayCtx {
  return useContext(Ctx) ?? { ...DEFAULT_DISPLAY, set: () => {} };
}

// ---------------------------------------------------------------------------
// The control itself — a compact "Net ▾" pill that opens a 3-toggle panel.

export function GrossNetControl(): React.ReactElement {
  const d = useDisplayOpts();
  const [open, setOpen] = useState(false);
  const isDefault = d.refunds === "net" && d.tax === "exclude" && d.shipping === "include";

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Choose what's counted in the numbers (refunds, sales tax, shipping)"
        style={pill}
      >
        Revenue adjustments{isDefault ? "" : " ·"} <span style={{ opacity: 0.6 }}>▾</span>
      </button>
      {open && (
        <>
          {/* click-away catcher */}
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
          <div style={panel}>
            <div style={panelTitle}>What&apos;s counted in the numbers</div>
            <ToggleRow
              label="Subtract refunds"
              sub="show what you actually kept"
              checked={d.refunds === "net"}
              onChange={(v) => d.set({ refunds: v ? "net" : "gross" })}
            />
            <ToggleRow
              label="Exclude sales tax"
              sub="tax is pass-through, not revenue"
              checked={d.tax === "exclude"}
              onChange={(v) => d.set({ tax: v ? "exclude" : "include" })}
            />
            <ToggleRow
              label="Include shipping"
              sub="count shipping charged as revenue"
              checked={d.shipping === "include"}
              onChange={(v) => d.set({ shipping: v ? "include" : "exclude" })}
            />
          </div>
        </>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  sub,
  checked,
  onChange,
}: {
  label: string;
  sub: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): React.ReactElement {
  return (
    <label style={row}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 15, height: 15, cursor: "pointer", marginTop: 1 }} />
      <span style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)" }}>{label}</span>
        <span style={{ fontSize: 11, color: "var(--text-faint)" }}>{sub}</span>
      </span>
    </label>
  );
}

const pill: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  cursor: "pointer",
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--body)",
  border: "1px solid var(--border)",
  borderRadius: 999,
  padding: "5px 11px",
  background: "var(--surface)",
  whiteSpace: "nowrap",
};

const panel: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 6px)",
  right: 0,
  zIndex: 31,
  width: 240,
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
  padding: "10px 12px",
};

const panelTitle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-faint)",
  marginBottom: 8,
};

const row: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 9,
  cursor: "pointer",
  padding: "7px 0",
};
